import { clearMapCamera } from "@/components/shared/AssetMap/mapCameraHandoff";
import { supabase } from "@/service/supabaseClient";
import { useNotificationsStore } from "@/stores/notifications-store";
import { CustomerRole } from "@/types/customerRoles";
import type { Database } from "@/types/supabase";
import type { Session, User } from "@supabase/supabase-js";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export const useAuthStore = defineStore("auth", () => {
  const isLoggedIn = ref(false);
  const _user = ref<User | null>(null);
  const _session = ref<Session | null>(null);
  const _userRole = ref<CustomerRole>(CustomerRole.UNKNOWN);
  const _userCompany = ref<string | null>(null);
  const _userCompanyId = ref<number | null>(null);

  const _userEnterpriseIdentifier = ref<string | null>(null);
  const _isAdmin = ref(false);

  // Admin company impersonation state
  const _adminOverrideCompany = ref<{
    id: number;
    name: string;
    enterprise_identifier: string;
  } | null>(null);

  const login = async (user: User, session: Session) => {
    isLoggedIn.value = true;
    _user.value = user;
    _session.value = session;
    _userRole.value = await getUserRole();
    await checkAdminStatus();
    _userCompany.value = await getCompany();
    _userCompanyId.value = await getCompanyId();
    _userEnterpriseIdentifier.value = await getCompanyEnterpriseIdentifier();
    console.log(
      "Retrieved user informations: ",
      `role: ${_userRole.value}, isAdmin: ${_isAdmin.value}, company: ${_userCompany.value}, companyId: ${_userCompanyId.value}, enterpriseIdentifier: ${_userEnterpriseIdentifier.value}`,
    );
    // Wire notifications after successful login
    try {
      const notifications = useNotificationsStore();
      await notifications.init(user.id);
      await notifications.subscribe(user.id);
    } catch (e) {
      console.error("[auth] Failed to initialize notifications", e);
    }

    // Riallinea la registrazione push di QUESTO browser all'utente che si e
    // appena autenticato. Non chiede niente e non mostra niente: se il permesso
    // non e mai stato concesso esce subito. Serve perche una subscription puo
    // essere stata ruotata dal browser, o cancellata al logout precedente, e
    // senza questo passaggio le push smetterebbero di arrivare in silenzio.
    try {
      const { syncPushSubscription } = await import("@/service/pushNotifications");
      await syncPushSubscription(user.id);
    } catch (e) {
      console.error("[auth] Failed to sync the push subscription", e);
    }
  };

  const logout = async () => {
    // Prima del signOut, finche la sessione vale ancora: cancellare la riga in
    // push_subscriptions richiede di essere autenticati, e la RLS la lega a
    // questo utente. Su un computer condiviso e anche la cosa giusta da fare -
    // chi ha appena fatto logout non deve continuare a ricevere gli allarmi
    // della propria flotta su una macchina che sta usando qualcun altro.
    try {
      const { disablePush } = await import("@/service/pushNotifications");
      await disablePush();
    } catch (e) {
      console.error("[auth] Failed to remove the push subscription", e);
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Error logging out:", error.message);
      return;
    }
    // Tear down notifications on logout
    try {
      const notifications = useNotificationsStore();
      await notifications.unsubscribe();
      notifications.$reset();
    } catch (e) {
      console.error("[auth] Failed to teardown notifications", e);
    }
    // Tear down asset store on logout
    try {
      const { useAssetStore } = await import("@/stores/useAssetStore");
      const assetStore = useAssetStore();
      assetStore.unsubscribeFromRealtime();
      assetStore.clear();
    } catch (e) {
      console.error("[auth] Failed to teardown asset store", e);
    }
    // Drop the parked map viewport: the next login must open on the globe, not
    // on the fleet of whoever was signed in before.
    clearMapCamera();

    isLoggedIn.value = false;
    _user.value = null;
    _session.value = null;
    _userRole.value = CustomerRole.UNKNOWN;
    _userCompany.value = null;
    _isAdmin.value = false;
    _adminOverrideCompany.value = null;
  };

  const getUser = () => {
    return _user.value;
  };

  const getCompany = async () => {
    if (!_user.value?.id) {
      return null;
    }

    const { data, error } = await supabase
      .from("companies_users")
      .select(
        `
        companies (
          name
        )
      `,
      )
      .eq("user", _user.value.id)
      .limit(1);

    if (error) {
      console.error("Error fetching company:", error.message);
      return null;
    }

    _userCompany.value = data[0]?.companies?.name || null;
    return data[0]?.companies?.name || null;
  };

  const hasCompany = () => {
    return _userCompany.value !== null && _userCompany.value !== undefined;
  };

  const hasEffectiveCompany = computed(
    () =>
      _adminOverrideCompany.value !== null ||
      (_userCompany.value !== null && _userCompany.value !== undefined),
  );

  /**
   * Fetches the user's company ID from the database.
   * Caches the result in the store for subsequent calls.
   *
   * @returns {Promise<number | null>} The company ID or null if not found or on error.
   * @throws {Error} On fetch failure (logged, but not thrown to caller).
   */
  const getCompanyId = async (): Promise<number | null> => {
    if (!_user.value?.id) {
      return null;
    }

    if (_userCompanyId.value !== null) {
      return _userCompanyId.value;
    }

    const { data, error } = await supabase
      .from("companies_users")
      .select(
        `
        companies (
          id
        )
      `,
      )
      .eq("user", _user.value.id)
      .limit(1);

    if (error) {
      console.error("Error fetching company ID:", error.message);
      return null;
    }

    _userCompanyId.value = data[0]?.companies?.id ?? null;
    return _userCompanyId.value;
  };

  /**
   * Fetches the user's company enterprise identifier from the database.
   * Caches the result in memory for subsequent calls.
   *
   * @returns {Promise<string | null>} The enterprise identifier or null if not found or on error.
   */
  const getCompanyEnterpriseIdentifier = async (): Promise<string | null> => {
    if (!_user.value?.id) {
      return null;
    }

    const { data, error } = await supabase
      .from("companies_users")
      .select(
        `
        companies (
          enterprise_identifier
        )
      `,
      )
      .eq("user", _user.value.id)
      .limit(1);

    if (error) {
      console.error(
        "Error fetching company enterprise identifier:",
        error.message,
      );
      return null;
    }

    return data[0]?.companies?.enterprise_identifier ?? null;
  };

  const getUserRole = async (): Promise<CustomerRole> => {
    // If user is not logged in, return unknown
    if (!_user.value?.id) {
      return CustomerRole.UNKNOWN;
    }

    // If user role is already set, return it
    if (_userRole.value !== CustomerRole.UNKNOWN) {
      return _userRole.value;
    }

    // Query customers_users table for user role
    const { data, error, count } = await supabase
      .from("companies_users")
      .select("role")
      .eq("user", _user.value?.id)
      .limit(1);

    // User with no role or company
    if (count === 0) {
      console.log(
        `No role found for user ${_user.value?.email} with id ${_user.value?.id}`,
      );
      return CustomerRole.UNKNOWN;
    }

    // Error fetching user role
    if (error) {
      console.error("Error fetching user role:", error.message);
      return CustomerRole.UNKNOWN;
    }

    // Set user role
    let role = CustomerRole.UNKNOWN;
    switch (data[0]?.role) {
      case "service":
        role = CustomerRole.SERVICE;
        break;
      case "planner":
        role = CustomerRole.PLANNER;
        break;
      case "user":
        role = CustomerRole.USER;
        break;
      case "owner":
        role = CustomerRole.OWNER;
        break;
      default:
        role = CustomerRole.UNKNOWN;
    }
    _userRole.value = role;
    return role;
  };

  const checkAdminStatus = async (): Promise<boolean> => {
    // If user is not logged in, return false (not admin)
    if (!_user.value?.id) {
      _isAdmin.value = false;
      return false;
    }

    // Query admins table for user admin status
    const { count, error } = await supabase
      .from("admins")
      .select("*", { count: "exact", head: true })
      .eq("user_id", _user.value.id);

    // Error checking admin status
    if (error) {
      console.error("Error checking admin status:", error.message);
      _isAdmin.value = false;
      return false;
    }

    // Set admin status if count is not null and greater than 0
    _isAdmin.value = count !== null && count > 0;
    return _isAdmin.value;
  };

  // Initialize auth state
  const initAuth = async () => {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error("Error getting session:", error.message);
        return;
      }

      if (session?.user) {
        await login(session.user, session);
        _userRole.value = await getUserRole();
        _userCompany.value = await getCompany();
        // Ensure notifications are active after session restore
        try {
          const notifications = useNotificationsStore();
          await notifications.init(session.user.id);
          await notifications.subscribe(session.user.id);
        } catch (e) {
          console.error(
            "[auth] Failed to initialize notifications on session restore",
            e,
          );
        }
      }
    } catch (err) {
      console.error("Error initializing auth:", err);
    }
  };

  // Listen for auth state changes
  supabase.auth.onAuthStateChange((event, session) => {
    if (session?.user) {
      login(session.user, session);
    } else {
      // Clean notifications when auth is cleared
      try {
        const notifications = useNotificationsStore();
        notifications.unsubscribe();
        notifications.$reset();
      } catch (e) {
        console.error(
          "[auth] Failed to teardown notifications on auth clear",
          e,
        );
      }
      isLoggedIn.value = false;
      _user.value = null;
      _session.value = null;
    }
  });

  // Admin company impersonation methods
  const setAdminOverrideCompany = (company: {
    id: number;
    name: string;
    enterprise_identifier: string;
  }) => {
    if (_isAdmin.value) {
      _adminOverrideCompany.value = company;
      console.log("[auth] Admin impersonating company:", company.name);
    }
  };

  const clearAdminOverrideCompany = () => {
    _adminOverrideCompany.value = null;
    console.log("[auth] Admin exited company impersonation");
  };

  // Effective getters (returns override if set, otherwise user's actual company)
  const getEffectiveCompanyName = () =>
    _adminOverrideCompany.value?.name ?? _userCompany.value;
  const getEffectiveCompanyId = () =>
    _adminOverrideCompany.value?.id ?? _userCompanyId.value;
  const getEffectiveCompanyEnterpriseIdentifier = () =>
    _adminOverrideCompany.value?.enterprise_identifier ??
    _userEnterpriseIdentifier.value;

  return {
    isLoggedIn,
    isAdmin: computed(() => _isAdmin.value),
    userRole: computed(() => _userRole.value),
    isImpersonatingCompany: computed(
      () => _isAdmin.value && _adminOverrideCompany.value !== null,
    ),
    login,
    logout,
    initAuth,
    getUser,
    getSession: () => _session.value,
    getCompany,
    getUserRole,
    hasCompany,
    hasEffectiveCompany,
    getCompanyId,
    getCompanyEnterpriseIdentifier,
    checkAdminStatus,
    // Admin impersonation
    setAdminOverrideCompany,
    clearAdminOverrideCompany,
    getEffectiveCompanyName,
    getEffectiveCompanyId,
    getEffectiveCompanyEnterpriseIdentifier,
  };
});
